'use client';

import { ArrowLeft, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { cn } from '@ui/lib/utils';

import { ResponsiveModal } from '../../../features/selection/responsiveModal';
import TickCircle from '../../../icons/TickCircle';
import { Button } from '../../../ui/button';
import { StarRating } from '../starRating/StarRating';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    let timeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(check, 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, []);
  return isMobile;
}

export interface ReviewVerifyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  userName: string;
  activityTitle?: string;
  date: string;
  rating: number;
  comment: string;
  tags?: string[];
  isSubmitting?: boolean;
  title?: string;
  approveLabel?: string;
  rejectLabel?: string;
  userLikedLabel?: string;
  className?: string;
}

export const ReviewVerifyDialog = ({
  isOpen,
  onClose,
  onApprove,
  onReject,
  userName,
  activityTitle,
  date,
  rating,
  comment,
  tags,
  isSubmitting = false,
  title = 'Customer Experience',
  approveLabel = 'Approve Review',
  rejectLabel = 'Reject Review',
  userLikedLabel = 'User liked:',
  className
}: ReviewVerifyDialogProps) => {
  const isMobile = useIsMobile();

  if (isMobile && isOpen) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-100">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer p-1.5 rounded-full hover:bg-neutral-100 transition-colors"
          >
            <ArrowLeft className="size-5 text-neutral-700" />
          </button>
          <h1 className="text-base font-semibold text-neutral-900">{title}</h1>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 py-5">
          <ReviewContent
            userName={userName}
            activityTitle={activityTitle}
            date={date}
            rating={rating}
            comment={comment}
            tags={tags}
            userLikedLabel={userLikedLabel}
            className={className}
          />
        </div>

        {/* Fixed bottom bar */}
        <div className="flex items-center justify-between gap-3 px-4 py-4 border-t border-neutral-200 bg-white">
          <button
            type="button"
            onClick={onReject}
            disabled={isSubmitting}
            className="cursor-pointer text-sm font-semibold text-red-500 hover:underline disabled:opacity-50"
          >
            {rejectLabel}
          </button>
          <Button onClick={onApprove} disabled={isSubmitting} className="bg-green-500 hover:bg-green-600 text-white">
            {approveLabel}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      showCloseButton={false}
      className="flex flex-col gap-4 p-6"
      dialogClassName="sm:max-w-md p-0"
      drawerClassName="p-0 rounded-t-xl h-[90vh]"
    >
      <div className={cn('flex flex-col gap-4 p-6', className)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer shrink-0 text-neutral-500 hover:text-neutral-800 transition-colors"
            >
              <ArrowLeft className="size-5" />
            </button>
            <h3 className="text-lg font-semibold text-neutral-800">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer shrink-0 text-neutral-500 hover:text-neutral-800 transition-colors"
          >
            <X className="size-5" />
          </button>
        </div>

        <ReviewContent
          userName={userName}
          activityTitle={activityTitle}
          date={date}
          rating={rating}
          comment={comment}
          tags={tags}
          userLikedLabel={userLikedLabel}
        />

        <div className="flex items-center justify-between gap-3 pt-2">
          <button
            type="button"
            onClick={onReject}
            disabled={isSubmitting}
            className="cursor-pointer text-sm font-semibold text-red-500 hover:underline disabled:opacity-50"
          >
            {rejectLabel}
          </button>
          <Button onClick={onApprove} disabled={isSubmitting} className="bg-green-500 hover:bg-green-600 text-white">
            {approveLabel}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
};

function ReviewContent({
  userName,
  activityTitle,
  date,
  rating,
  comment,
  tags,
  userLikedLabel,
  className
}: {
  userName: string;
  activityTitle?: string;
  date: string;
  rating: number;
  comment: string;
  tags?: string[];
  userLikedLabel?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className="flex flex-col gap-1">
        <p className="text-lg font-medium text-neutral-800">{userName}</p>
        {(activityTitle || date) && (
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            {activityTitle && <span>{activityTitle}</span>}
            {activityTitle && date && <span className="text-neutral-300">|</span>}
            {date && <span>{date}</span>}
          </div>
        )}
      </div>

      <StarRating value={rating} size="md" readonly />

      {comment && <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap">{comment}</p>}

      {tags && tags.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-neutral-600">{userLikedLabel}</p>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 bg-green-50 border border-green-200 rounded-lg px-2 py-1 text-xs text-green-700"
              >
                <TickCircle className="size-3 shrink-0" />
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
